/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {Stack, Text, Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useItemVariant} from '.'
import {useProducts, useProduct} from '@salesforce/commerce-sdk-react'
import {getDisplayVariationValues} from '@salesforce/retail-react-app/app/utils/product-utils'

/**
 * In the context of a cart product item variant, this component renders a styled
 * list of the selected variation values as well as any promos (w/ info popover).
 */
const ItemAttributes = ({includeQuantity, fontSize = 'sm', ...props}) => {
    const variant = useItemVariant()
    const intl = useIntl()

    const isGiftCardVariant = Boolean(
        variant?.c_isEGiftCard || variant?.c_isGiftCard
    )

    // Check if all variationAttributes have values arrays before calling getDisplayVariationValues
    const hasValidVariationAttributes = variant?.variationAttributes?.every(attr =>
        attr.values && Array.isArray(attr.values)
    )

    const variationValues = hasValidVariationAttributes && variant?.variationValues
        ? getDisplayVariationValues(
            variant.variationAttributes,
            variant.variationValues
        )
        : null

    if (isGiftCardVariant && variationValues) {
        delete variationValues.Color;
    }
    // get variant info for bundled products in cart page and order history page
    const productBundleIds =
        variant?.bundledProductItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: productBundleVariantData, isLoading: bundleVariantIsLoading} = useProducts(
        {
            parameters: {
                ids: productBundleIds,
                allImages: false
            }
        },
        {
            enabled: Boolean(variant?.type?.bundle && productBundleIds),
            select: (result) => {
                // formats response so we can easily display child quantity/variant selection
                return result?.data?.map((item) => {
                    const quantity = variant?.bundledProductItems.find(
                        (childProduct) => childProduct.productId === item.id
                    )?.quantity
                    return {
                        ...item,
                        quantity,
                        variationValues: item?.variationAttributes?.every(attr => attr.values && Array.isArray(attr.values)) && item?.variationValues
                            ? getDisplayVariationValues(
                                item.variationAttributes,
                                item.variationValues
                            )
                            : null
                    }
                })
            }
        }
    )

    // get bundle product data for wishlist page
    const {data: productBundleData, isLoading: productBundleIsLoading} = useProduct(
        {
            parameters: {
                id: variant?.id,
                allImages: false
            }
        },
        {
            enabled: Boolean(variant?.type?.bundle && !productBundleIds)
        }
    )

    return (
        <Stack spacing={0} flex={1} minWidth={0} {...props}>
            {variationValues &&
                Object.keys(variationValues).map((key) => (
                    <Text
                        lineHeight="18px"
                        color="black"
                        fontSize={fontSize}
                        key={`${key}: ${variationValues[key]}`}
                        noOfLines={1}
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        maxWidth="100%"
                        data-testid={`product-${key.toLowerCase()}`}
                    >
                        {key}: {variationValues[key]}
                    </Text>
                ))}

            {includeQuantity && (
                <Text
                    lineHeight="18px"
                    color="black"
                    fontSize={fontSize}
                    noOfLines={1}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    maxWidth="100%"
                >
                    <FormattedMessage
                        defaultMessage="Quantity: {quantity}"
                        values={{quantity: variant?.quantity}}
                        id="item_attributes.label.quantity"
                    />
                </Text>
            )}

            {!productBundleIsLoading && productBundleData && !productBundleVariantData && (
                <Box>
                    {productBundleData?.bundledProducts.map(({product, quantity}) => (
                        <Box marginTop={2} key={product.id}>
                            <Text fontSize={fontSize} color="gray.700" as="b">
                                {product?.name}
                            </Text>
                            <Text fontSize={fontSize} color="gray.700">
                                {intl.formatMessage({
                                    defaultMessage: 'Qty',
                                    id: 'add_to_cart_modal.label.quantity'
                                })}
                                : {quantity}
                            </Text>
                        </Box>
                    ))}
                </Box>
            )}

            {!bundleVariantIsLoading && productBundleVariantData && (
                <Box>
                    <Text fontSize={fontSize} marginTop={3} fontWeight={500}>
                        {intl.formatMessage({
                            defaultMessage: 'Selected Options',
                            id: 'item_attributes.label.selected_options'
                        })}
                        :
                    </Text>
                    {productBundleVariantData?.map(
                        ({variationValues, name: productName, quantity, id}) => {
                            return (
                                <Box key={id} marginTop={2}>
                                    <Text fontSize={fontSize} color="gray.700" as="b">
                                        {productName}
                                    </Text>
                                    <Text fontSize={fontSize} color="gray.700">
                                        {intl.formatMessage({
                                            defaultMessage: 'Qty',
                                            id: 'add_to_cart_modal.label.quantity'
                                        })}
                                        : {quantity}
                                    </Text>
                                    {variationValues && Object.keys(variationValues).map((key) => {
                                        const selectedVariant = `${key}: ${variationValues[key]}`
                                        return (
                                            <Text
                                                fontSize={fontSize}
                                                color="gray.700"
                                                key={selectedVariant}
                                            >
                                                {selectedVariant}
                                            </Text>
                                        )
                                    })}
                                </Box>
                            )
                        }
                    )}
                </Box>
            )}
        </Stack>
    )
}

ItemAttributes.propTypes = {
    includeQuantity: PropTypes.bool,
    fontSize: PropTypes.string
}

export default ItemAttributes
